import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';

export interface StyledButtonProps {
  onClick?: () => void;
  onClickAsync?: () => Promise<void>;

  design: 'default' | 'primary' | 'error';
  type: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  rootClassName?: string;
  className?: string;
  tooltip?: string;
  pulse?: boolean;

  children?: React.ReactNode;
}

export function StyledButton(props: StyledButtonProps) {
  const { type, pulse, onClick, onClickAsync, children, disabled } = props;

  const [isClicking, setIsClicking] = useState<boolean>(false);

  const onClickInvoke = useCallback(async () => {
    if (onClick) {
      return onClick();
    }

    if (onClickAsync) {
      try {
        setIsClicking(true);

        await onClickAsync();
      } finally {
        setIsClicking(false);
      }
    }
  }, [onClick, onClickAsync]);

  const isPrimary = props.design && props.design === 'primary';
  const isError = props.design && props.design === 'error';

  const isDefault = !isPrimary && !isError;

  const [tooltipId] = useState<string>(uuidv4());

  return (
    <div
      className={classNames(
        'inline-flex',
        props.rootClassName && props.rootClassName
      )}
    >
      {props.tooltip && <Tooltip id={tooltipId}></Tooltip>}

      <button
        type={type}
        onClick={onClickInvoke}
        disabled={isClicking || disabled}
        data-tooltip-id={tooltipId}
        data-tooltip-content={props.tooltip}
        className={classNames(
          'flex rounded-full border font-light px-4 py-2 shadow-xs select-none', //frame + alignments
          'disabled:opacity-50', //effects

          'outline-offset-1 ring-offset-transparent ring-offset-1 ring-1', //set outline-2 to show outline, always set color

          /** STYLE */
          isPrimary && 'bg-brand-pink outline-brand-pink text-white',
          isPrimary &&
            !disabled &&
            'focus:ring-brand-pink hover:ring-brand-pink',

          isError && 'bg-red-800 outline-red-800 text-white',
          isError && !disabled && 'hover:ring-red-700 focus:ring-red-700',

          isDefault && 'bg-white  ring-transparent outline-gray-500 text-black',
          isDefault && !disabled && 'focus:ring-gray-500 hover:ring-gray-500',

          !disabled && pulse && 'outline-2 outline-solid animate-pulse-outline',
          props.className && props.className
        )}
      >
        {children}
      </button>
    </div>
  );
}

export default StyledButton;
